/**
 * Extended statistics components for Phase 2
 * Societal context and cosmic/natural perspective
 */

import { SocietalStats, CosmicStats, formatCompactNumber, formatNumber } from '../utils/calculations';
import { getTopEvents } from '../data/worldEvents';

export interface ExtendedStatsOptions {
    societalStats: SocietalStats;
    cosmicStats: CosmicStats;
    birthYear: number;
}

/**
 * Create the societal context section
 */
export function createSocietalSection(options: ExtendedStatsOptions): HTMLElement {
    const { societalStats, birthYear } = options;
    const events = getTopEvents(birthYear, 8);

    const container = document.createElement('div');
    container.className = 'extended-stats-section glass-card';

    const header = document.createElement('div');
    header.className = 'extended-stats-header';
    header.innerHTML = `
        <h3>Your place in humanity</h3>
        <button class="collapse-toggle" aria-expanded="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M6 9l6 6 6-6"/>
            </svg>
        </button>
    `;
    container.appendChild(header);

    const content = document.createElement('div');
    content.className = 'extended-stats-content';

    // Prose paragraphs
    const proseSection = document.createElement('div');
    proseSection.className = 'societal-prose';

    proseSection.innerHTML = `
        <p>The average person will meet around <strong>80,000 people</strong> in their lifetime. 
        You've likely already crossed paths with approximately <strong>${formatNumber(societalStats.peopleEncountered)}</strong> individuals.</p>
        
        <p>Since your birth, humanity has collectively experienced approximately 
        <strong>${formatNumber(societalStats.birthsDuringLife)}</strong> births 
        and <strong>${formatNumber(societalStats.deathsDuringLife)}</strong> deaths.</p>
    `;

    content.appendChild(proseSection);

    // World events section
    if (events.length > 0) {
        const eventsSection = document.createElement('div');
        eventsSection.className = 'world-events';

        const eventsTitle = document.createElement('h4');
        eventsTitle.textContent = 'World events during your lifetime';
        eventsSection.appendChild(eventsTitle);

        const eventsList = document.createElement('div');
        eventsList.className = 'events-list';

        events.forEach(event => {
            const eventItem = document.createElement('div');
            eventItem.className = 'event-item';
            eventItem.innerHTML = `
                <span class="event-year">${event.year}</span>
                <span class="event-title">${event.title}</span>
            `;
            eventsList.appendChild(eventItem);
        });

        eventsSection.appendChild(eventsList);
        content.appendChild(eventsSection);
    }

    container.appendChild(content);

    // Add collapse toggle functionality
    header.querySelector('.collapse-toggle')?.addEventListener('click', () => {
        const isExpanded = content.style.display !== 'none';
        content.style.display = isExpanded ? 'none' : 'block';
        header.querySelector('.collapse-toggle')?.setAttribute('aria-expanded', (!isExpanded).toString());
        header.querySelector('svg')!.style.transform = isExpanded ? 'rotate(-90deg)' : 'rotate(0)';
    });

    return container;
}

/**
 * Create the cosmic/natural perspective section
 */
export function createCosmicSection(options: ExtendedStatsOptions): HTMLElement {
    const { cosmicStats } = options;

    const container = document.createElement('div');
    container.className = 'extended-stats-section glass-card';

    const header = document.createElement('div');
    header.className = 'extended-stats-header';
    header.innerHTML = `
        <h3>Your cosmic journey</h3>
        <button class="collapse-toggle" aria-expanded="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M6 9l6 6 6-6"/>
            </svg>
        </button>
    `;
    container.appendChild(header);

    const content = document.createElement('div');
    content.className = 'extended-stats-content';

    const statsGrid = document.createElement('div');
    statsGrid.className = 'cosmic-stats-grid';

    // Distance through space with orbit icon
    const distanceCard = document.createElement('div');
    distanceCard.className = 'cosmic-stat-card';
    distanceCard.innerHTML = `
        <div class="cosmic-icon orbit-icon">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="1.5">
                <circle cx="24" cy="24" r="20" stroke-dasharray="4 2"/>
                <circle cx="24" cy="24" r="4" fill="var(--accent)"/>
                <g class="orbiting-planet">
                    <circle cx="44" cy="24" r="3" fill="var(--foreground)"/>
                </g>
            </svg>
        </div>
        <div class="cosmic-stat-value">${formatCompactNumber(cosmicStats.distanceThroughSpace)} km</div>
        <div class="cosmic-stat-label">Distance traveled through space</div>
        <div class="cosmic-stat-detail">Riding Earth around the Sun</div>
    `;
    statsGrid.appendChild(distanceCard);

    // Sunshine hours with sun icon
    const sunshineCard = document.createElement('div');
    sunshineCard.className = 'cosmic-stat-card';
    sunshineCard.innerHTML = `
        <div class="cosmic-icon sunshine-icon">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="1.5">
                <circle cx="24" cy="24" r="10" fill="var(--accent)" stroke="none"/>
                <line x1="24" y1="4" x2="24" y2="10"/>
                <line x1="24" y1="38" x2="24" y2="44"/>
                <line x1="4" y1="24" x2="10" y2="24"/>
                <line x1="38" y1="24" x2="44" y2="24"/>
                <line x1="9.9" y1="9.9" x2="14.1" y2="14.1"/>
                <line x1="33.9" y1="33.9" x2="38.1" y2="38.1"/>
                <line x1="9.9" y1="38.1" x2="14.1" y2="33.9"/>
                <line x1="33.9" y1="14.1" x2="38.1" y2="9.9"/>
            </svg>
        </div>
        <div class="cosmic-stat-value">${formatNumber(cosmicStats.sunshineHoursExperienced)}</div>
        <div class="cosmic-stat-label">Hours of sunshine</div>
        <div class="cosmic-stat-detail">Based on your region's average</div>
    `;
    statsGrid.appendChild(sunshineCard);

    content.appendChild(statsGrid);
    container.appendChild(content);

    // Add collapse toggle functionality
    header.querySelector('.collapse-toggle')?.addEventListener('click', () => {
        const isExpanded = content.style.display !== 'none';
        content.style.display = isExpanded ? 'none' : 'block';
        header.querySelector('.collapse-toggle')?.setAttribute('aria-expanded', (!isExpanded).toString());
        header.querySelector('svg')!.style.transform = isExpanded ? 'rotate(-90deg)' : 'rotate(0)';
    });

    return container;
}
